import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import Box from '@cloudscape-design/components/box';
import Drawer from '@cloudscape-design/components/drawer';
import { TicketTable, SortState } from '../components/TicketTable';
import { SyncDataModal } from '../components/SyncDataModal';
import { FilterDrawer } from '../components/FilterDrawer';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { TicketSearchBar } from '../components/TicketSearchBar';
import { PopularSearches } from '../components/PopularSearches';
import { useTicketFilters } from '../hooks/useTicketFilters';
import { listTickets, searchTickets, getLastSyncTime, batchResummarize, getQueueStatus } from '../api/tickets';
import { Ticket } from '../types/ticket';
import { recordEvent } from '../rum';

const PAGE_SIZE = 20;
const DEFAULT_SORT: SortState = { sortingColumn: { sortingField: 'createdAt' }, isDescending: true };

export function TicketListPage() {
  const navigate = useNavigate();
  const { filters, setFilter, removeFilter, clearAll, applyPreset, toApiParams, activeFilterCount, hasActiveFilters } = useTicketFilters();
  const [sorting, setSorting] = useState<SortState>(DEFAULT_SORT);
  const [currentPage, setCurrentPage] = useState(1);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState('');
  const [selectedItems, setSelectedItems] = useState<Ticket[]>([]);
  const [resummarizing, setResummarizing] = useState(false);
  const [resummarizingIds, setResummarizingIds] = useState<Set<string>>(new Set());
  const [flashMessage, setFlashMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; text: string } | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Load last synced time
  useEffect(() => {
    getLastSyncTime()
      .then((ts) => setLastRefreshed(ts || ''))
      .catch(() => {});
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sortBy = sorting.sortingColumn.sortingField || 'createdAt';
      const sortOrder = sorting.isDescending ? 'desc' : 'asc';
      const params = toApiParams(currentPage - 1, PAGE_SIZE, sortBy, sortOrder);

      let result;
      if (params.q) {
        const { q, ...rest } = params;
        result = await searchTickets(q, rest as any);
      } else {
        result = await listTickets(params);
      }

      setTickets(result.tickets);
      setTotal(result.total ?? result.tickets.length);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, sorting, currentPage, toApiParams]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleSortChange = useCallback((state: SortState) => {
    setSorting(state);
    setCurrentPage(1);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const status = await getQueueStatus();
        const remaining = status.queue.messagesAvailable + status.queue.messagesInFlight;
        const dlqCount = status.dlq.messagesAvailable;
        if (remaining > 0) {
          setFlashMessage({ type: 'info', text: `Re-summarizing: ${remaining} ticket${remaining > 1 ? 's' : ''} remaining...` });
        } else {
          stopPolling();
          setResummarizingIds(new Set());
          fetchTickets();
          if (dlqCount > 0) {
            setFlashMessage({ type: 'warning', text: `Re-summarization complete. ${dlqCount} ticket${dlqCount > 1 ? 's' : ''} failed.` });
          } else {
            setFlashMessage({ type: 'success', text: 'Re-summarization complete.' });
          }
        }
      } catch {
        stopPolling();
      }
    }, 10000);
  }, [stopPolling, fetchTickets]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleBatchResummarize = async () => {
    setResummarizing(true);
    setFlashMessage(null);
    const count = selectedItems.length;
    recordEvent('resummarize', { count });
    try {
      await batchResummarize(selectedItems.map((t) => t.ticketId));
      setResummarizingIds(new Set(selectedItems.map((t) => t.ticketId)));
      setSelectedItems([]);
      setFlashMessage({ type: 'info', text: `Re-summarizing ${count} ticket${count > 1 ? 's' : ''}...` });
      startPolling();
    } catch (e: any) {
      setFlashMessage({ type: 'error', text: `Failed to queue re-summarization: ${e.message}` });
    } finally {
      setResummarizing(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '0' }}>
      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <SpaceBetween size="s">
          <Header
            variant="h1"
            counter={`(${total})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="normal" onClick={handleBatchResummarize} loading={resummarizing} disabled={selectedItems.length === 0} iconName="gen-ai">
                  Re-summarize{selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}
                </Button>
                <Button variant="normal" iconName="settings" onClick={() => navigate('/settings')}>
                  Settings
                </Button>
                <Button variant="primary" iconName="refresh" onClick={() => { recordEvent('sync_trigger', { source: 'tickets' }); setSyncModalVisible(true); }}>
                  Sync data
                </Button>
              </SpaceBetween>
            }
            description={lastRefreshed ? `Last synced: ${new Date(lastRefreshed).toLocaleString()}` : undefined}
          >
            Tickets
          </Header>

          {flashMessage && <Alert type={flashMessage.type}>{flashMessage.text}</Alert>}

          <TicketSearchBar
            filters={filters}
            onSearchTextChange={(text) => setFilter('searchText', text)}
            onSearchScopeChange={(scope) => setFilter('searchScope', scope)}
            onToggleFilters={() => setDrawerOpen(!drawerOpen)}
            filtersOpen={drawerOpen}
            activeFilterCount={activeFilterCount}
            matchCount={total}
          />

          <ActiveFilterChips
            filters={filters}
            onRemoveFilter={removeFilter}
            onClearAll={clearAll}
          />

          <PopularSearches onPreset={applyPreset} />

          <TicketTable
            tickets={tickets}
            loading={loading}
            error={error}
            onRetry={fetchTickets}
            sortingState={sorting}
            onSortingChange={handleSortChange}
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            resummarizingIds={resummarizingIds}
          />

          {total > PAGE_SIZE && (
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={Math.ceil(total / PAGE_SIZE)}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          )}
        </SpaceBetween>

        <SyncDataModal
          visible={syncModalVisible}
          onDismiss={() => { setSyncModalVisible(false); getLastSyncTime().then((ts) => setLastRefreshed(ts || '')).catch(() => {}); }}
          onSyncStarted={() => getLastSyncTime().then((ts) => setLastRefreshed(ts || '')).catch(() => {})}
        />
      </div>

      {/* Filter drawer (right side) */}
      {drawerOpen && (
        <div style={{
          width: '320px',
          flexShrink: 0,
          borderLeft: '1px solid #e9ebed',
          padding: '16px',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 120px)',
        }}>
          <FilterDrawer
            filters={filters}
            onChange={setFilter}
            onClear={clearAll}
            onClose={() => setDrawerOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
